import React, { useState, useEffect, useRef } from 'react';

// Dynamically importing assets from the local assets directory
import banner1 from '../assets/banner5.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';
import banner5 from '../assets/banner5.jpg';
import banner6 from '../assets/banner3.jpg';

const ServicesBanner1 = () => {
  const sliderImages = [banner1, banner2, banner3, banner4, banner5, banner6];
  
  // Center slide tracking state (Defaulted to balance perfectly on index 2)
  const [activeIndex, setActiveIndex] = useState(2);
  const trackRef = useRef(null);

  // Automated cinematic loop shifting center focus sequentially every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  // Cluster 1 Structural Content
  const foodServices = [
    { title: 'Fruit & Vegetable Dehydration', icon: '🍃', img: banner1, desc: 'Advanced dehydration preserving maximum nutritional value and extending product shelf life.' },
    { title: 'Healthy Food Production', icon: '📦', img: banner2, desc: 'Manufacturing high-quality, stable, and completely natural functional food products.' },
    { title: 'Value Chain Development', icon: '📈', img: banner3, desc: 'Optimizing agricultural pathways from local Tanzanian farms directly to consumer markets.' },
    { title: 'Post-Harvest Loss Solutions', icon: '🛡️', img: banner4, desc: 'Deploying strategic mitigation technologies to safeguard seasonal harvests and farmers yields.' },
    { title: 'Farmer Sourcing Programs', icon: '🤝', img: banner5, desc: 'Building sustainable corporate outgrower partnerships and fair-trade operational frameworks.' }
  ];

  // Cluster 2 Structural Content
  const waterServices = [
    { title: 'Borehole & Well Drilling', icon: '💧', img: banner6, desc: 'Professional hydrological surveying and deep borehole drilling for reliable supply.' },
    { title: 'Water Supply Systems', icon: '🚰', img: banner6, desc: 'Complete engineering, piping networks, and storage infrastructure asset installation.' },
    { title: 'Irrigation Infrastructure', icon: '🌱', img: banner5, desc: 'Tailored irrigation solutions to support commercial agriculture and continuous farm yields.' },
    { title: 'Pump Systems Maintenance', icon: '⚙️', img: banner1, desc: 'Precision industrial pump installation, proactive servicing, and electrical optimization.' },
    { title: 'Community Water Access', icon: '🌍', img: banner2, desc: 'Scalable structural distribution networks engineered for villages, schools, and corporate plants.' }
  ];

  return (
    <section style={styles.servicesSection} id="divisions" className="py-5 overflow-hidden">
      <div className="container my-lg-4">
        
        {/* Minimalist Section Header */}
        <div className="row align-items-end mb-5 pb-3">
          <div className="col-12 col-md-7 text-start">
            <div style={styles.sectionLabel} className="text-uppercase mb-2">Operations Portfolio</div>
            <h2 style={styles.sectionTitle} className="fw-medium text-white m-0">Our Business Divisions</h2>
          </div>
          <div className="col-12 col-md-5 text-start text-md-end mt-3 mt-md-0">
            <span style={styles.taglineText}>From Farm to Future · From Water to Prosperity</span>
          </div>
        </div>

        {/* ── CLUSTER 2: Water Well Drilling & Water Infrastructure ── */}
        <div className="row g-4 pt-4 mb-5">
          {/* Hero Column Placed First: Naturally stays on top for Mobile view */}
          <div className="col-12 col-lg-4 order-lg-1">
            <div style={{ ...styles.clusterHeroCard, backgroundImage: `url(${banner6})` }} className="p-4 d-flex flex-column justify-content-between h-100">
              <div style={styles.heroOverlayWater}></div>
              <div style={{ zIndex: 2 }}>
                <span style={styles.heroNumLabel}>DIVISION_01</span>
                <h3 style={styles.heroClusterTitle} className="mt-2 text-white fw-bold">Water Infrastructure & Drilling</h3>
              </div>
              <p style={styles.heroClusterDesc} className="m-0 text-white">
                Providing specialized drilling options to secure clean, continuous, and highly structured water resource pipelines.
              </p>
            </div>
          </div>

          {/* Cards Column Placed Second: Renders below Hero on Mobile, pushes to the right (order-lg-2) on Desktop */}
          <div className="col-12 col-lg-8 order-lg-2">
            <div className="row g-3">
              {waterServices.map((service, index) => (
                <div className="col-12 col-sm-6" key={index}>
                  <div 
                    style={styles.modernMiniCard}
                    className="p-4 d-flex flex-column justify-content-between h-100 position-relative overflow-hidden"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#38A169';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(30, 45, 74, 0.7)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div style={styles.miniIconBoxWater}>{service.icon}</div>
                      <h4 style={styles.miniCardTitleWater} className="m-0 fw-bold">{service.title}</h4>
                    </div>
                    <p style={styles.miniCardDesc} className="m-0">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CLUSTER 1: Food Processing & Value Addition ── */}
        <div className="row g-4 mt-5 pb-5">
          {/* Hero is already on top naturally for mobile view */}
          <div className="col-12 col-lg-4">
            <div style={{ ...styles.clusterHeroCard, backgroundImage: `url(${banner1})` }} className="p-4 d-flex flex-column justify-content-between h-100">
              <div style={styles.heroOverlayFood}></div>
              <div style={{ zIndex: 2 }}>
                <span style={styles.heroNumLabel}>DIVISION_02</span>
                <h3 style={styles.heroClusterTitle} className="mt-2 text-white fw-bold">Food Processing & Value Addition</h3>
              </div>
              <p style={styles.heroClusterDesc} className="m-0 text-white">
                Transforming the agricultural value chain across Tanzania through modern food processing techniques.
              </p>
            </div>
          </div>

          <div className="col-12 col-lg-8">
            <div className="row g-3">
              {foodServices.map((service, index) => (
                <div className="col-12 col-sm-6" key={index}>
                  <div 
                    style={styles.modernMiniCard}
                    className="p-4 d-flex flex-column justify-content-between h-100 position-relative overflow-hidden"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#ED8936';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(30, 45, 74, 0.7)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div style={styles.miniIconBoxFood}>{service.icon}</div>
                      <h4 style={styles.miniCardTitleFood} className="m-0 fw-bold">{service.title}</h4>
                    </div>
                    <p style={styles.miniCardDesc} className="m-0">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* ── PLACED BELOW SERVICES: Cinematic Full-Width Variable-Grayscale Center Slider ── */}
      <div style={styles.sliderWrapper} className="position-relative mt-5 pt-4">
        <div style={styles.sliderMaskLeft}></div>
        <div style={styles.sliderMaskRight}></div>
        
        <div 
          ref={trackRef}
          style={{
            ...styles.sliderTrack,
            // Math dynamic centering logic: calculates viewport center minus dynamic card dimension offsets
            transform: `translateX(calc(50vw - 212px - (${activeIndex} * 444px)))`
          }}
        >
          {sliderImages.map((img, idx) => {
            const isCenter = idx === activeIndex;
            return (
              <div 
                key={idx}
                style={{
                  ...styles.sliderSlide,
                  backgroundImage: `url(${img})`,
                  filter: isCenter ? 'grayscale(0%) contrast(1.05)' : 'grayscale(100%) opacity(0.25)',
                  transform: isCenter ? 'scale(1.04)' : 'scale(0.94)',
                  borderColor: isCenter ? '#ED8936' : 'rgba(30, 45, 74, 0.45)',
                  boxShadow: isCenter ? '0 20px 40px rgba(0, 0, 0, 0.5)' : 'none'
                }}
                onClick={() => setActiveIndex(idx)}
              >
                <div style={isCenter ? styles.slideOverlayActive : styles.slideOverlayInactive}></div>
              </div>
            );
          })}
        </div>
        
        {/* Minimalist Navigation Pagination Indicators */}
        <div style={styles.tickTrack} className="d-flex justify-content-center gap-2 mt-4">
          {sliderImages.map((_, idx) => (
            <span 
              key={idx}
              style={{
                ...styles.tickIndicator,
                backgroundColor: idx === activeIndex ? '#ED8936' : 'rgba(30, 45, 74, 0.6)',
                width: idx === activeIndex ? '28px' : '6px'
              }}
              onClick={() => setActiveIndex(idx)}
            />
          ))}
        </div>
      </div>

    </section>
  );
};

const styles = {
  servicesSection: {
    backgroundColor: '#0A0F18',
    fontFamily: "'Libre Franklin', sans-serif",
  },
  sectionLabel: {
    fontSize: '11px',
    color: '#ED8936',
    letterSpacing: '0.15em',
    fontWeight: '600',
  },
  sectionTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'calc(1.8rem + 1.2vw)',
  },
  taglineText: {
    fontSize: '12px',
    color: '#718096',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  clusterHeroCard: {
    minHeight: '380px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '4px',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid rgba(30, 45, 74, 0.4)',
  },
  heroOverlayFood: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(10, 15, 24, 0.4) 0%, rgba(33, 46, 32, 0.9) 100%)',
    zIndex: 1,
  },
  heroOverlayWater: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(10, 15, 24, 0.4) 0%, rgba(30, 36, 33, 0.9) 100%)',
    zIndex: 1,
  },
  heroNumLabel: {
    fontSize: '10px',
    letterSpacing: '0.15em',
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '600',
  },
  heroClusterTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '28px',
    lineHeight: '1.2',
  },
  heroClusterDesc: {
    zIndex: 2,
    fontSize: '13.5px',
    lineHeight: '1.6',
    color: '#E2E8F0',
    opacity: 0.95,
  },
  modernMiniCard: {
    backgroundColor: '#111A2E',
    border: '1px solid rgba(30, 45, 74, 0.7)',
    borderRadius: '4px',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  miniIconBoxFood: {
    width: '38px',
    height: '38px',
    backgroundColor: 'rgba(237, 137, 54, 0.1)',
    border: '1px solid rgba(237, 137, 54, 0.25)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  miniIconBoxWater: {
    width: '38px',
    height: '38px',
    backgroundColor: 'rgba(56, 161, 105, 0.1)',
    border: '1px solid rgba(56, 161, 105, 0.25)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  miniCardTitleFood: {
    fontSize: '16.5px',
    color: '#ffffff',
    letterSpacing: '0.01em',
  },
  miniCardTitleWater: {
    fontSize: '16.5px',
    color: '#ffffff',
    letterSpacing: '0.01em',
  },
  miniCardDesc: {
    fontSize: '13.5px',
    color: '#A0AEC0',
    lineHeight: '1.65',
    fontWeight: '300',
  },
  
  /* ── Slider Specific Enhancements ── */
  sliderWrapper: {
    width: '100vw',
    backgroundColor: '#060A10',
    borderTop: '1px solid rgba(30, 45, 74, 0.5)',
    borderBottom: '1px solid rgba(30, 45, 74, 0.5)',
    padding: '48px 0',
  },
  sliderTrack: {
    display: 'flex',
    gap: '24px',
    width: 'max-content',
    transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
    willChange: 'transform',
  },
  sliderSlide: {
    width: '420px',
    height: '270px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '4px',
    border: '1px solid rgba(30, 45, 74, 0.2)',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'filter 0.6s ease, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.6s ease, box-shadow 0.6s ease',
  },
  slideOverlayActive: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, transparent 60%, rgba(10, 15, 24, 0.5) 100%)',
  },
  slideOverlayInactive: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(10, 15, 24, 0.35)',
  },
  sliderMaskLeft: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '15vw',
    background: 'linear-gradient(90deg, #060A10 0%, transparent 100%)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  sliderMaskRight: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '15vw',
    background: 'linear-gradient(270deg, #060A10 0%, transparent 100%)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  tickTrack: {
    width: '100%',
    position: 'relative',
    zIndex: 4,
  },
  tickIndicator: {
    height: '4px',
    borderRadius: '2px',
    display: 'inline-block',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
  },
};

export default ServicesBanner1;